import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import { LoadingButton } from '@mui/lab';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPendingOrderInfo, getPreviousOrderInfo, removeCartCheckedFromRedux } from '../../../../redux/slices/orders';
import { removeOrderDelay } from '../../../../api/orders';
import { useDispatch, useSelector } from '../../../../redux/store';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { fDate, timestamp } from '../../../../utils/formatTime';
import { useTabs } from '../../../../context/TabContext';
import Iconify from '../../../../reusable-components/iconify/Iconify';
import { getUnreadData } from '../../../../redux/slices/dashboard';
import { fCurrency } from '../../../../utils/formatNumber';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import VendorDate from './VendorDate';
import axiosInstance from '../../../../utils/axios';
import { objectToFormData } from '../../../../api/utils';
import AltAddressDialog from './AltAddressDialog';
import { markMetricRemoved, NotificationMetricType } from '../../../../redux/slices/notifications';

ConfirmOrderDialog.propTypes = {
  id: PropTypes.string,
  allReqDates: PropTypes.array,
  openConfirm: PropTypes.bool,
  setOpenConfirm: PropTypes.func,
  goToNextOrder: PropTypes.func,
};

export default function ConfirmOrderDialog({ id, allReqDates, openConfirm, setOpenConfirm, goToNextOrder }) {
  const { orderById } = useSelector((state) => state.orders || {});
  const {
    items = [],
    orderNumber,
    vendorMinimums,
    requestedShipDate,
    facilityAddress,
    orderDelay,
    vendorDateMessages,
    facility,
    vendors,
    globalNote,
    removeGlobalNoteFromVendors,
    submittedBy,
    vendorDates,
  } = orderById[id] || {};
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter');
  const navigate = useNavigate();
  const { addNewTabAndNavigate } = useTabs();
  const [openDialog, setOpenDialog] = useState(false);
  const [orderDelayUntil, setOrderDelayUntil] = useState(orderDelay?.sendOrderAt);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [showOrderDelayUntil, setShowOrderDelayUntil] = useState(false);
  const [removingDelay, setRemovingDelay] = useState(false);
  const orderQueue = JSON.parse(localStorage.getItem('selectedOrders'));

  const nextOrderInQueueOrNavigate = (url) => {
    if (orderQueue?.length) goToNextOrder();
    else navigate(url);
  };

  const processOrderFunction = () => {
    setProcessingOrder(true);
    dispatch(removeCartCheckedFromRedux());
    axiosInstance
      .post(
        '/ApproveOrder',
        objectToFormData({
          items: items
            ?.filter((item) => item.approved && item.deleted !== true)
            ?.map((item) => ({
              id: item.id,
              vendorId: item.vendorId,
              quantity: item.quantity,
              uomAmount: item.uomAmount,
              itemId: item.itemId,
              agoraCategoryId: item.agoraCategory.id,
              agoraSubcategoryId: item.agoraSubcategory.id,
              vendorItemId: item.vendorItemId,
              description: item.description,
              uomType: item.uomType,
              price: item.price,
              approved: item.approved,
              hasOrderCategoryOverride: item.hasOrderCategoryOverride,
              substituted: item.substituted,
              replacedById: item.replacedWithVendorItemId,
            })),
          rerunOrder: filter === 'rerun',
          orderId: id,
          address,
          delayOrderInSeconds: orderDelayUntil
            ? Math.floor(Math.abs(new Date().getTime() - orderDelayUntil.getTime()) / 1000)
            : null,
          globalNote,
          removeGlobalNoteFromVendors,
        })
      )
      .then((res) => {
        const { success, message } = res.data;
        if (!success && message === 'Something has gone wrong') enqueueSnackbar(message, { variant: 'error' });
        else if (success && message === 'Order Delayed') {
          enqueueSnackbar(`Order successfully set. Order will be sent on ${timestamp(orderDelayUntil)}`, {
            variant: 'success',
          });
          nextOrderInQueueOrNavigate(`/dashboard/orders?filter=pending`);
        } else {
          const fullSuccess = message === 'Success';
          dispatch(markMetricRemoved(NotificationMetricType.OrdersNeedApproval, id));
          dispatch(getPreviousOrderInfo(id));
          enqueueSnackbar(
            `${!fullSuccess ? `${message}\n` : ''}Order #${orderNumber || id} has been ` +
              `${fullSuccess ? 'successfully' : 'partially'} processed!`,
            {
              variant: fullSuccess ? 'success' : 'error',
              style: { whiteSpace: 'pre-wrap' },
              autoHideDuration: 3000,
              action: (key) => (
                <>
                  {!orderQueue?.length ? (
                    <Button
                      onClick={() => addNewTabAndNavigate(`/dashboard/orders/${id}?filter=previous`)}
                      color={fullSuccess ? 'secondary' : 'primary'}
                      disabled={orderQueue?.length}
                      endIcon={<Iconify icon="material-symbols:open-in-new" />}
                    >
                      #{orderNumber || id}
                    </Button>
                  ) : (
                    `#${orderNumber || id}`
                  )}
                  <Button onClick={() => closeSnackbar(key)} sx={{ color: '#fff' }}>
                    X
                  </Button>
                </>
              ),
            }
          );
          if (!fullSuccess) nextOrderInQueueOrNavigate(`/dashboard/orders/${id}?filter=previous`);
          else goToNextOrder();
          dispatch(getUnreadData());
        }
      })
      .catch(() => enqueueSnackbar('Error processing order', { variant: 'error' }))
      .finally(() => setProcessingOrder(false));
  };

  useEffect(() => {
    setOrderDelayUntil(orderDelay ? new Date(orderDelay.sendOrderAt) : null);
  }, [orderDelay]);

  const handleRemoveOrderDelay = () => {
    setRemovingDelay(true);
    removeOrderDelay(id)
      .then((res) => {
        if (res.data) {
          enqueueSnackbar('Delay removed successfully', { variant: 'success' });
          setShowOrderDelayUntil(false);
          dispatch(getPendingOrderInfo(id));
        } else {
          enqueueSnackbar('Error removing delay', { variant: 'error' });
        }
      })
      .catch(() => enqueueSnackbar('Error removing delay', { variant: 'error' }))
      .finally(() => setRemovingDelay(false));
  };

  const vendorTotals = items
    .filter((item) => item.approved)
    .reduce((acc, item) => {
      const { vendorId, price, quantity, vendor } = item;
      const total = price * quantity;
      const vendorTotal = acc.find((total) => total.vendorId === vendorId);
      if (vendorTotal) {
        vendorTotal.total += total;
        return acc;
      }
      acc.push({
        vendorId,
        vendor,
        total,
        allowsRequestedShipDate: vendors?.find((vendor) => vendor.id === item.vendorId)?.allowsRequestedShipDate,
        minimum: vendorMinimums?.find((min) => min.vendorId === vendorId)?.minimum ?? 0,
        requestedDate: allReqDates.find((date) => date.vendorId === vendorId)?.date,
        messages:
          vendorDateMessages?.filter(
            (message) => message.vendorId === vendorId && message.shipDate
          ) || [],
      });
      return acc;
    }, []);

  const address = orderById[id]?.orderAddress || facilityAddress;

  const uniqueVendors = useMemo(() => {
    const vendorIds = new Set(items?.map((item) => item.vendorId));
    return vendors?.filter((vendor) => vendorIds?.has(vendor.id));
  }, [items, vendors]);

  const allowAddressEdits = uniqueVendors?.length > 0 && uniqueVendors?.every((v) => v.allowsAddressEdits);

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  return (
    <>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={openConfirm}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setOpenConfirm(false);
        }}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Box color="info.main">
            {showOrderDelayUntil && (
              <IconButton
                onClick={() => {
                  setOrderDelayUntil(orderDelay?.sendOrderAt ? new Date(orderDelay?.sendOrderAt) : null);
                  setShowOrderDelayUntil(false);
                }}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                  fontWeight: 'bold',
                }}
              >
                <Iconify icon="ep:arrow-left-bold" width={24} height={24} />
              </IconButton>
            )}
            Order Summary <CloseIconButton onClick={() => setOpenConfirm(false)} />
          </Box>
          <Box sx={{ fontSize: '15px', fontWeight: 'normal' }}>
            {facility}{' '}
            <Box color="info.main" component="span">
              |
            </Box>{' '}
            PO#: {orderNumber}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ typography: 'body2' }}>
          {!showOrderDelayUntil && (
            <>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }} color="info.main">
                  Delivery Order Address:
                </Typography>

                <Tooltip title={allowAddressEdits ? 'Edit delivery address' : ''}>
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      if (allowAddressEdits) {
                        handleClickOpen();
                      }
                    }}
                    sx={{
                      mt: 0.5,
                      cursor: allowAddressEdits ? 'pointer' : 'default',
                      textDecoration: allowAddressEdits ? 'underline' : 'none',
                      display: 'inline-flex',
                    }}
                  >
                    {`${address?.street || ''} ${address?.city || ''} ${address?.state || ''} ${
                      address?.zip || ''
                    }`.trim()}
                    {allowAddressEdits && <Iconify icon="mdi:pencil" width={16} height={16} sx={{ ml: 0.5 }} />}
                  </Box>
                </Tooltip>
              </Box>
              <Divider sx={{ bgcolor: 'rgb(145, 158, 171)' }} />
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }} color="info.main">
                  Vendors:
                </Typography>
                {requestedShipDate && (
                  <Tooltip
                    title="This date hasn't been sent to the vendor yet. Update the ship date in the order to send to the vendor."
                    arrow
                    placement="top"
                  >
                    <Box color="secondary.main" sx={{ width: 'fit-content' }}>
                      {submittedBy?.name || 'User'} requested ship date: {fDate(requestedShipDate, 'MMMM dd')}
                    </Box>
                  </Tooltip>
                )}
                {vendorTotals.map((vendorTotal, index) => (
                  <Box key={index}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {vendorTotal.vendor}:
                    </Typography>
                    <VendorDate item={vendorTotal} allReqDates={allReqDates} disableEdits vendorDates={vendorDates} />
                    <Box sx={{ ml: 1 }}>
                      <Box display="flex" alignItems="center">
                        Total: {fCurrency(vendorTotal.total)}
                        <Tooltip
                          title={
                            vendorTotal.total >= vendorTotal.minimum ? 'Order meets minimum' : 'Order below minimum'
                          }
                        >
                          <Iconify
                            icon={
                              vendorTotal.total >= vendorTotal.minimum
                                ? 'material-symbols:check-circle-outline'
                                : 'mdi:minus-circle-outline'
                            }
                            style={{ marginLeft: '4px', cursor: 'auto', width: '17px', height: '17px' }}
                            color={vendorTotal.total >= vendorTotal.minimum ? 'primary' : 'secondary'}
                          />
                        </Tooltip>
                      </Box>
                      {vendorTotal.minimum > 0 && (
                        <Typography variant="body2">Vendor Minimum: ${vendorTotal.minimum}</Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {(orderDelay || showOrderDelayUntil) && filter !== 'rerun' && (
            <Box sx={orderDelay && !showOrderDelayUntil ? { mt: 2 } : {}}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }} color="info.main">
                Delay Order:
              </Typography>
              {orderDelay && (
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="secondary" sx={{ fontSize: '13px' }}>
                    Order delayed by: {orderDelay.addedBy}
                  </Typography>
                  <LoadingButton
                    variant="text"
                    size="small"
                    color="error"
                    loading={removingDelay}
                    onClick={handleRemoveOrderDelay}
                    startIcon={<Iconify icon="mdi:delete-outline" width={16} height={16} />}
                    sx={{
                      textTransform: 'none',
                      textDecoration: 'underline',
                      minWidth: 'auto',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                      '& .MuiButton-startIcon': {
                        marginRight: '3px',
                        position: 'relative',
                        top: '-1px',
                      },
                    }}
                  >
                    Remove Delay
                  </LoadingButton>
                </Box>
              )}
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                Send order on
              </Typography>
              <DateTimePicker
                minDate={new Date()}
                slotProps={{
                  textField: { size: 'small', sx: { width: '100%' } },
                  actionBar: { actions: ['clear', 'cancel', 'accept'] },
                }}
                value={orderDelayUntil || null}
                onAccept={(dateValue) => {
                  setOrderDelayUntil(dateValue);
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!showOrderDelayUntil && filter !== 'rerun' && (
            <Button variant="outlined" color="primary" onClick={() => setShowOrderDelayUntil(true)}>
              Delay Order
            </Button>
          )}
          <LoadingButton
            color="primary"
            size="small"
            variant="contained"
            loading={processingOrder}
            sx={{ mr: 1 }}
            onClick={processOrderFunction}
          >
            Process order
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <AltAddressDialog open={openDialog} setOpenDialog={setOpenDialog} handleCloseDialog={handleCloseDialog} />
    </>
  );
}
